import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppRouter } from './AppRouter';
import { installGlobalErrorHandlers } from './monitoring';
import { initSentry, installSentryReporter } from './sentry';
import { initWebVitals } from './webVitals';
import { AppErrorBoundary } from './SentryErrorBoundary';

// Phase 7c — design tokens. Imported once at the entry point so the
// custom properties propagate to every CSS module via :root.
import './styles/tokens.css';

// Auth + onboarding typography. The intake itself stays on
// Public Sans (USWDS-style government feel — matches the form's
// dense, regulatory shape). The auth surfaces use a distinctive
// editorial pairing: Fraunces (variable serif, beautiful numerals
// for the "what's next" steps + headline gravitas) and DM Sans
// (clean modern sans for UI). @fontsource-variable processes these
// at build time into Vite-handled woff2 assets served from /assets/
// — same-origin so CSP `font-src 'self'` keeps holding.
import '@fontsource-variable/fraunces';
import '@fontsource-variable/dm-sans';

// Phase 6b — initialize Sentry BEFORE mounting React so any error
// thrown during the first render is captured. When VITE_SENTRY_DSN
// is unset (local dev without a Sentry project) `initSentry`
// returns false and we skip reporter wiring; the console fallback
// from `monitoring.ts` keeps signal visible during dev.
const viteEnv = (import.meta as unknown as {
  env: Record<string, string | undefined>;
}).env;
if (
  initSentry({
    dsn: viteEnv?.['VITE_SENTRY_DSN'],
    environment: viteEnv?.['MODE'] ?? 'production',
    // Optional release tag — when set, Sentry links events to this
    // build. Inject via Vite's `define` config or a build-time
    // env var (e.g., a CI script that exports the git short SHA
    // as VITE_APP_VERSION). Undefined is fine; events still flow.
    release: viteEnv?.['VITE_APP_VERSION'],
  })
) {
  installSentryReporter();
}

// Funnel uncaught errors and unhandled rejections
// into the monitoring stub before any UI mounts. After
// `installSentryReporter` runs the active reporter is Sentry, so
// these handlers route to Sentry too.
installGlobalErrorHandlers();

// Core Web Vitals (LCP/CLS/INP/FCP/TTFB)
// emit through the same `reportEvent` seam as funnel telemetry, so
// they ship as Sentry breadcrumbs on the next error AND can be
// re-routed to a product-analytics adapter later without touching
// the observer code. Initialized after Sentry so the breadcrumbs
// land in the active reporter (Sentry when DSN is set; console
// fallback in dev).
initWebVitals();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootElement).render(
  <StrictMode>
    <AppErrorBoundary>
      <AppRouter />
    </AppErrorBoundary>
  </StrictMode>,
);
